import { useContext } from "react";
import {Button } from "react-bootstrap";
import { ReviewContext } from "../ReviewPatient";
import {
  BIRTH_PLACE,
  BMI,
  CURRENT_GESTATION,
  DATE,
  DIAGNOSIS_DATE,
  DOB,
  DOWNLOAD_EMR,
  DUE_DATE,
  EMAIL,
  ETHNICITY,
  FAMILY_NAME,
  GESTATION_DIABETES,
  GIVEN_NAME,
  HEIGHT,
  INDIGENOUS_STATUS,
  LANGUAGE,
  MESSAGE,
  MESSAGE_READ,
  MOBILE_NUMBER,
  NO,
  NOTES,
  PRE_PREGNANCY_WEIGHT,
  RESEARCH,
  SALUTATION,
  TEAM,
  URN,
  WEEKDAYS,
  WEIGHT,
  YES,
} from "../../util/const";
import {
  CalculateBMI,
  CalculateDiagnosisDate,
} from "../../util/utils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Bgl, Insulin } from "../../types/observation";
import { FormatDateForDisplay, Initialise, SetTimeslotForElement } from "../../util/data-utils";

const EMR = () => {
  const context = useContext(ReviewContext);
  const patient = context.patient;

  const BGL = "bgl";
  const INSULIN = "insulin";

  var today = new Date();
  var day = today.getDay() + 1; // day as weekday number

  var bglMap: Map<string, Array<Bgl>> = new Map<string, Array<Bgl>>();
  var insulinMap: Map<string, Map<string, Array<Insulin>>> = new Map<
    string,
    Map<string, Array<Insulin>>
  >();

  const generatePDF = () => {
    const doc = new jsPDF();

    // Add your text content to the PDF here
    doc.setFontSize(20);
    doc.text(
      `MoTHer Gestational Diabetes Record for ${patient.salutation} ${patient.givenNames} ${patient.familyName}`,
      10,
      10
    );

    doc.setFontSize(10);

    const marginLeft = 10;
    const marginTop = 20;
    const rowHeight = 10;
    const columnWidth = 60;

    const data = [
      { label: `${URN}`, value: patient.urn },
      { label: `${TEAM}`, value: patient.team },
      { label: `${RESEARCH}`, value: patient.group },
      { label: `${SALUTATION}`, value: patient.salutation },
      { label: `${GIVEN_NAME}`, value: patient.givenNames },
      { label: `${FAMILY_NAME}`, value: patient.familyName },
      { label: `${DOB}`, value: FormatDateForDisplay(patient.birthDate) },
      { label: `${BIRTH_PLACE}`, value: patient.birthPlace },
      { label: `${LANGUAGE}`, value: patient.language },
      { label: `${ETHNICITY}`, value: patient.ethnicity },
      { label: `${MOBILE_NUMBER}`, value: patient.phone },
      { label: `${EMAIL}`, value: patient.email },
      { label: `${INDIGENOUS_STATUS}`, value: patient.indigenousStatus },
      { label: `${DUE_DATE}`, value: FormatDateForDisplay(patient.dueDate) },
      { label: `${CURRENT_GESTATION}`, value: patient.currentGestation },
      { label: `${GESTATION_DIABETES}`, value: patient.diabetesGestation },
      {
        label: `${DIAGNOSIS_DATE}`,
        value: CalculateDiagnosisDate(
          patient.startDate,
          patient.currentGestation,
          patient.diabetesGestation
        ),
      },
      { label: `${PRE_PREGNANCY_WEIGHT}`, value: patient.weight },
      { label: `${HEIGHT}`, value: patient.height },
      { label: `${WEIGHT}`, value: patient.weight },
      { label: `${BMI}`, value: CalculateBMI(patient.height, patient.weight) },
    ];

    data.forEach((item, index) => {
      const row = Math.floor(index / 3);
      const column = index % 3;
      const x = marginLeft + column * columnWidth;
      const y = marginTop + row * rowHeight;

      doc.text(`${item.label}: ${item.value}`, x, y);
    });

    if (patient.observations !== undefined && patient.observations.size > 0) {
      var bgls: Array<Bgl> | undefined;
      if (patient.observations.has(BGL)) bgls = patient.observations.get(BGL) as Array<Bgl>;
      let hasBgls = bgls !== undefined && bgls.length > 0;

      var insulins: Array<Insulin> | undefined;
      if (patient.observations.has(INSULIN))
        insulins = patient.observations.get(INSULIN) as Array<Insulin>;
      let hasInsulins = insulins !== undefined && insulins.length > 0;

      Initialise(patient);

      if (hasBgls) {
        bgls?.forEach((element) => {
          SetTimeslotForElement(element, patient);
          let key = WEEKDAYS[element.weekDay];
          if(bglMap.has(key))
          {
            bglMap.get(key)!.push(element);
          }
          else
          {
            bglMap.set(key, [element])
          }
          // var aDayBgls: Array<Bgl>;
          // if (bglMap.has(key)) aDayBgls = bglMap.get(key)!;
          // else aDayBgls = new Array<Bgl>();
          // aDayBgls?.push(element);
          // bglMap.set(key, aDayBgls);
        });
      }

      if (hasInsulins) {
        insulins?.forEach((element) => {
          SetTimeslotForElement(element, patient);
          let key = WEEKDAYS[element.weekDay];

          if (insulinMap.has(key)) {
            var aDayData = insulinMap.get(key);
            if (aDayData?.has(element.context)) {
              aDayData.get(element.context)?.push(element);
            } else {
              aDayData?.set(element.context, [element]);
            }
          } else {
            var temp: Map<string, Array<Insulin>> = new Map<string, Array<Insulin>>();
            temp.set(element.context, [element]);
            insulinMap.set(key, temp);
          }
        });
      }
    }

    const insulinTable: string[][] = [];

    WEEKDAYS.map(() => {
      day = day - 1;
      if (day < 0) {
        day = 7 + day;
      }
      var key = WEEKDAYS[day];

      let insulinTypeMap = insulinMap.get(key) || new Map();

      // Prepare the row data for the current day
      let rowData = [key];

      // Add insulin data
      ["Morning", "Before Breakfast", "Before Lunch", "Before Dinner", "Bedtime"].forEach((key) => {
        let doseInfo: string[] = [];
        let insulinList = insulinTypeMap.get(key) || [];
        insulinList.forEach((insulin: Insulin) => {
          if (insulin.name) doseInfo.push(`${insulin.name}: ${insulin.dose} units`);
          if (insulin.metforminDose) doseInfo.push(`Metformin: ${insulin.metforminDose} mg`);
        });
        rowData.push(doseInfo.join(", "));
      });
      insulinTable.push(rowData);
    });

    const bglTable: string[][] = [];
    WEEKDAYS.map(() => {
      day = day - 1;
      if (day < 0) {
        day = 7 + day;
      }
      var key = WEEKDAYS[day];

      let bglList = bglMap.get(key) || [];

      let rowData = [key,"","","","",""];

      // Add BGL data
      
      ["Overnight", "Fasting", "After Breakfast", "After Lunch", "After Dinner"].forEach((time) => {
        // let bgl = bglList.find((bgl, index) => bgl.context === time);
        // if (bgl) {
        //   let bglValue = bgl.reading;
        //   rowData.push(bglValue);
        // } else {
        //   rowData.push("");
        // }
        bglList.forEach((bgl) => {
          let bglValue = bgl.reading;
          if(bgl.overnight !== undefined && bgl.overnight.length > 0) {
            rowData[1] = bglValue;
          } 
          if(bgl.fasting !== undefined && bgl.fasting.length > 0) {
            rowData[2] = bglValue;
          }
          if(bgl.postBreakfast !== undefined && bgl.postBreakfast.length > 0) {
            rowData[3] = bglValue;
          } 
          if(bgl.postLunch !== undefined && bgl.postLunch.length > 0) {
            rowData[4] = bglValue;
          }
          if(bgl.postDinner !== undefined && bgl.postDinner.length > 0) {
            rowData[5] = bglValue;
          }
        });
      });

      bglTable.push(rowData);
    });

    // // Add remarks
    // let notes = bglList
    //   .filter((bgl) => bgl.notes)
    //   .map(
    //     (bgl) =>
    //       `${
    //         bgl.fasting || bgl.postBreakfast || bgl.postLunch || bgl.postDinner || bgl.beforeBed
    //       } (${FormatTimeForDisplay(bgl.recordedTime)}): ${bgl.notes}`
    //   )
    //   .join(", ");
    // rowData.push(notes);

    let startY = marginTop + Math.ceil(data.length / 3) * rowHeight + 10;

    // Add the Insulin title before the table
    doc.setFontSize(16);
    doc.text(
      `Insulin and Metformin (Week Ending: ${FormatDateForDisplay(context.weekEnding)})`,
      marginLeft,
      startY
    );

    // Define the columns
    const insulinColumns = [
      { header: "Day", dataKey: "day" },
      { header: "Morning", dataKey: "morning" },
      { header: "Before Breakfast", dataKey: "beforeBreakfast" },
      { header: "Before Lunch", dataKey: "beforeLunch" },
      { header: "Before Dinner", dataKey: "beforeDinner" },
      { header: "Before Bed", dataKey: "bedtime" },
    ];

    autoTable(doc, {
      head: [insulinColumns.map((col) => col.header)],
      body: insulinTable,
      startY: startY + 5,
      theme: "grid",
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold" },
    });

    // Add the title before the table
    startY = (doc as any).lastAutoTable.finalY + 20;
    doc.setFontSize(16);
    doc.text(
      `BGL Table (Week Ending: ${FormatDateForDisplay(context.weekEnding)})`,
      marginLeft,
      startY
    );

    // Define the columns
    const bglColumns = [
      { header: "Day", dataKey: "day" },
      { header: "Overnight", dataKey: "overnight" },
      { header: "Fasting", dataKey: "fasting" },
      { header: "Post Breakfast", dataKey: "breakfast" },
      { header: "Post Lunch", dataKey: "lunch" },
      { header: "Post Dinner", dataKey: "dinner" },
    ];

    autoTable(doc, {
      head: [bglColumns.map((col) => col.header)],
      body: bglTable,
      startY: startY + 5,
      theme: "grid",
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold" },
    });

    // Add the history message
    let textContent: string[][] = [];

    if (patient.reviews) {
      patient.reviews.forEach((element) => {
        if (element.message || element.note) {
          const date = FormatDateForDisplay(element.reviewDate);
          const message = element.message || "";
          const messageRead = element.messageRead ? YES : NO;
          const note = element.note || "";

          textContent.push([date, message, messageRead, note]);
        }
      });
    }

    if (textContent.length > 0) {
      // Add the title before the table
      startY = (doc as any).lastAutoTable.finalY + 20;
      doc.setFontSize(16);
      doc.text("History Messages", marginLeft, startY);

      // Add the history table
      const headers = [[DATE, MESSAGE, MESSAGE_READ, NOTES]];
      const rows = textContent;

      autoTable(doc, {
        head: headers,
        body: rows,
        startY: startY + 5,
        theme: "grid",
        headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold" },
      });
    }

    doc.save(`${patient.urn}.pdf`);
  };

  return (
    <Button type="button" className="btn-color me-2" onClick={generatePDF}>
      {DOWNLOAD_EMR}
    </Button>
  );
};
export default EMR;
